// Station "Home": one screen with my tally, talk keys, program and team.
// Colors: amber = talking (mine and others), red/green = tally only, channel color = identity.
import { SessionClient } from './session.js';
import { NATIVE_AUDIO, NativeSessionClient } from './native-session.js';
import { h, fill, textOn, auth, go, toast, modal, STATION } from './ui.js';
import { APP, APP_PLATFORM, native, appPrefs } from './native.js';

export function viewSession(sessionId, $app) {
  const client = NATIVE_AUDIO ? new NativeSessionClient({ token: auth.token, sessionId }) : new SessionClient({ token: auth.token, sessionId });
  window.__talk = client; // diagnostics
  let wakeLock = null;

  const root = h('div', { class: 'station' });
  const nameEl = h('span', { class: 'st-name' }, 'Session');
  const connEl = h('span', { class: 'st-conn' });
  const head = h(
    'header',
    { class: 'st-head' },
    h('a', { class: 'brand', href: '#/', 'aria-label': 'Back to sessions' }, h('span', { class: 'dot' })),
    nameEl,
    h('span', { class: 'spacer' }),
    connEl,
    h('button', { class: 'ghost small', 'aria-label': 'Audio settings', onclick: () => audioSettings() }, 'Audio'),
    h('button', { class: 'ghost small', onclick: () => leave() }, 'Leave'),
  );
  const tallyEl = h('section', { class: 'st-tally', 'aria-live': 'polite' });
  const callEl = h('div', { class: 'st-call', hidden: true });
  const talkEl = h('section', { class: 'st-talk', 'aria-label': 'Talk' });
  const pgmEl = h('section', { class: 'st-pgm', 'aria-label': 'Program' });
  const teamEl = h('section', { class: 'st-team', 'aria-label': 'Participants' });
  const video = h('video', { autoplay: true, playsinline: true, muted: true });
  client.mountVideo?.(video, root);
  const body = h('div', { class: 'st-body' }, h('div', { class: 'st-col' }, tallyEl, callEl, talkEl), h('div', { class: 'st-col' }, pgmEl, teamEl));

  // ---- join card ----
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const prefs = (() => {
    try {
      return JSON.parse(localStorage.getItem('talk.devices') || '{}');
    } catch {
      return {};
    }
  })();
  const savePrefs = () => localStorage.setItem('talk.devices', JSON.stringify(prefs));
  const testArea = h('div', { class: 'stack' });
  const startCard = h(
    'div',
    { class: 'center' },
    h(
      'div',
      { class: 'card narrow' },
      h('h1', {}, 'Ready?'),
      h('p', { class: 'muted' }, 'Microphone and audio are switched on when you join. Headphones recommended.', APP ? (APP_PLATFORM === 'ios' ? (NATIVE_AUDIO ? ' Audio runs natively, including with the screen locked. On iOS 18 or later, the Live Activity controls the channel, talk and receive mute. The microphone stays active while joined; audio is transmitted only while talking.' : ' The lock screen shows the session status. Keep the panel open for microphone access.') : ' Audio keeps running when you lock the screen. The headset button works as talk key.') : iOS ? ' Important on iPhone: do not lock the screen or leave the app – iOS stops the audio otherwise.' : ' The screen stays on during the session.'),
      testArea,
      h('button', { class: 'primary big', onclick: () => join() }, 'Join'),
    ),
  );
  // mic test before joining: choose input/output, see the level, play a test tone
  let testStream = null;
  let testRaf = 0;
  function stopTest() {
    cancelAnimationFrame(testRaf);
    testStream?.getTracks().forEach((t) => t.stop());
    testStream = null;
  }
  async function micTest() {
    stopTest();
    try {
      testStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, ...(prefs.mic ? { deviceId: { exact: prefs.mic } } : {}) } });
    } catch (e) {
      return fill(testArea, h('p', { class: 'hint' }, `No access to the microphone (${e.message}).`));
    }
    const devs = await navigator.mediaDevices.enumerateDevices();
    const ins = devs.filter((d) => d.kind === 'audioinput');
    const outs = devs.filter((d) => d.kind === 'audiooutput');
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    ctx.createMediaStreamSource(testStream).connect(an);
    const bar = h('i');
    const meter = h('div', { class: 'meter-big', role: 'meter', 'aria-label': 'Microphone level' }, bar);
    const buf = new Float32Array(an.fftSize);
    const tick = () => {
      an.getFloatTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v));
      bar.style.width = `${Math.min(100, peak * 140)}%`;
      testRaf = requestAnimationFrame(tick);
    };
    tick();
    const inSel = h('select', { id: 'mic-in', onchange: (e) => ((prefs.mic = e.target.value), savePrefs(), micTest()) }, ...ins.map((d) => h('option', { value: d.deviceId, selected: d.deviceId === (prefs.mic || testStream.getAudioTracks()[0].getSettings().deviceId) }, d.label || 'Microphone')));
    const outSel =
      SessionClient.canSelectOutput() && outs.length > 0 &&
      h('select', { id: 'audio-out', onchange: (e) => ((prefs.out = e.target.value), savePrefs()) }, ...outs.map((d) => h('option', { value: d.deviceId, selected: d.deviceId === (prefs.out || 'default') }, d.label || 'Output')));
    const tone = h('button', {
      class: 'small',
      onclick: async () => {
        const c = new (window.AudioContext || window.webkitAudioContext)();
        if (prefs.out && c.setSinkId) await c.setSinkId(prefs.out === 'default' ? '' : prefs.out).catch(() => {});
        const o = c.createOscillator();
        const g = c.createGain();
        g.gain.value = 0.15;
        o.frequency.value = 880;
        o.connect(g).connect(c.destination);
        o.start();
        setTimeout(() => (o.stop(), c.close()), 600);
      },
    }, 'Test tone');
    fill(
      testArea,
      h('label', { class: 'field', for: 'mic-in' }, h('span', { class: 'lbl' }, 'Microphone – speak, the bar should move'), inSel),
      meter,
      outSel ? h('label', { class: 'field', for: 'audio-out' }, h('span', { class: 'lbl' }, 'Output'), h('div', { class: 'row' }, outSel, tone)) : h('div', { class: 'row' }, tone, h('span', { class: 'muted small' }, 'Choose the output in the system settings.')),
    );
  }
  fill(testArea, h('button', { onclick: micTest }, 'Test microphone'));
  root.append(head, startCard);
  // the director panel in the Mac app joins right away (devices are set in Audio)
  if (STATION && !APP) setTimeout(() => join(), 0);
  $app.append(root);

  async function join() {
    stopTest();
    fill(startCard, h('div', { class: 'card narrow' }, 'Connecting …'));
    try {
      await client.start({ micDeviceId: prefs.mic || null, outputDeviceId: prefs.out || null });
    } catch (e) {
      fill(
        startCard,
        h(
          'div',
          { class: 'card narrow' },
          h('h1', {}, 'No microphone'),
          h('p', {}, `The microphone cannot be accessed (${e.message}). Without a microphone you can listen, see tally and receive the program.`),
          h('button', { class: 'primary', onclick: () => client.start({ withMic: false }).catch((err) => toast(err.message)) }, 'Join without microphone'),
          h('button', { onclick: () => location.reload() }, 'Retry'),
        ),
      );
      return;
    }
    try {
      wakeLock = await navigator.wakeLock?.request('screen');
    } catch {}
    // app: background mode, lock screen / notification, hardware talk key
    native.start({ title: nameEl.textContent, text: 'Connecting …', volumeKeys: appPrefs.volumeKeys }).catch((e) => toast(e.message));
  }
  const leave = async () => {
    native.stop();
    await client.leave();
    go('#/');
  };

  // ---- PTT: hold = talk, short tap (<300 ms) = latch, tap again = release ----
  const latched = new Set();
  const holds = new Map(); // pointerId -> { target, downAt }
  const releaseHold = (e) => {
    const hold = holds.get(e.pointerId);
    if (!hold) return;
    holds.delete(e.pointerId);
    if (performance.now() - hold.downAt < 300 && e.type === 'pointerup') latched.add(hold.target);
    else client.setTalk(hold.target, false);
    syncPtt();
  };
  window.addEventListener('pointerup', releaseHold);
  window.addEventListener('pointercancel', releaseHold);
  function ptt(el, target) {
    el.dataset.target = target;
    el.classList.add('ptt');
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (latched.has(target)) {
        latched.delete(target);
        client.setTalk(target, false);
        syncPtt();
        return;
      }
      holds.set(e.pointerId, { target, downAt: performance.now() });
      client.setTalk(target, true);
      navigator.vibrate?.(15);
    });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    return el;
  }
  // own mic level on the talking key
  let lvlRaf = 0;
  function levelLoop() {
    cancelAnimationFrame(lvlRaf);
    if (!client.talking.size) return root.style.setProperty('--lvl', '0');
    root.style.setProperty('--lvl', client.micLevel().toFixed(2));
    lvlRaf = requestAnimationFrame(levelLoop);
  }
  function syncPtt() {
    levelLoop();
    for (const t of [...latched]) if (!client.talking.has(t)) latched.delete(t);
    for (const el of root.querySelectorAll('.ptt')) {
      el.classList.toggle('talking', client.talking.has(el.dataset.target));
      el.classList.toggle('latched', latched.has(el.dataset.target));
      const state = el.querySelector('.state');
      if (state && client.talking.has(el.dataset.target)) state.textContent = latched.has(el.dataset.target) ? 'you talk · latched' : 'you talk';
    }
  }

  // ---- tally (mine) ----
  function myCam() {
    return (client.tally.cams || []).find((c) => c.id === client.me?.cam) || null;
  }
  function renderTally() {
    const cam = myCam();
    const state = cam?.state || 'off';
    root.classList.toggle('tally-program', state === 'program');
    root.classList.toggle('tally-preview', state === 'preview');
    tallyEl.className = `st-tally ${cam ? state : 'none'}`;
    if (!cam) return fill(tallyEl, h('span', { class: 'cam' }, 'No tally'), h('span', { class: 'what' }, 'no camera assigned to you'));
    fill(tallyEl, h('span', { class: 'cam' }, cam.label), h('span', { class: 'what' }, state === 'program' ? 'ON AIR' : state === 'preview' ? 'PREVIEW' : 'bereit'));
  }

  // ---- talk keys (structure rebuilt only on channel/permission change) ----
  let talkKey = '';
  const talkRefs = new Map();
  const openVol = new Set();
  function renderTalk() {
    const me = client.me;
    if (!me) return;
    const { channels, peers } = client.state;
    const mine = channels.filter((c) => me.canListen.includes(c.id) || me.canTalk.includes(c.id));
    const key = JSON.stringify([mine, me.canTalk, me.canListen]);
    if (key !== talkKey) {
      talkKey = key;
      talkRefs.clear();
      fill(
        talkEl,
        !mine.length && h('p', { class: 'muted small' }, 'No channels are assigned to your role.'),
        ...mine.map((c, i) => {
          const r = { who: h('span', { class: 'who' }), state: h('span', { class: 'state' }) };
          const canTalk = me.canTalk.includes(c.id);
          r.talk = h('button', { class: 'k-talk', disabled: !canTalk, 'aria-label': `Talk on ${c.name}` }, h('span', { class: 'k-name' }, c.name, c.priority && h('span', { class: 'prio' }, 'Priority'), i < 9 && h('kbd', {}, String(i + 1))), r.state, r.who);
          if (canTalk) ptt(r.talk, `ch:${c.id}`);
          r.listen = me.canListen.includes(c.id) && h('button', { class: 'k-listen', onclick: () => client.setListen(c.id, !client.me.listen.includes(c.id)).catch((e) => toast(e.message)) });
          r.volRow = volumeRow(c.id, c.name);
          r.volBtn = h('button', { class: 'k-vol', 'aria-label': `Volume ${c.name}`, onclick: () => (openVol.has(c.id) ? openVol.delete(c.id) : openVol.add(c.id), (r.volRow.hidden = !openVol.has(c.id))) }, 'Vol');
          r.sigBtn = canTalk && h('button', { class: 'k-sig', 'aria-label': `Call ${c.name}`, onclick: () => sendSignal(`ch:${c.id}`, c.name) }, h('span', { 'aria-hidden': 'true' }, '◉'), 'Call');
          r.volRow.hidden = !openVol.has(c.id);
          r.el = h('div', { class: `key ${c.priority ? 'priority' : ''}`, style: { '--c': c.color || '#888', '--ct': textOn(c.color || '#888') } }, h('div', { class: 'k-main' }, r.talk, h('div', { class: 'k-side' }, r.listen, r.sigBtn, h('div', { class: 'k-mini' }, r.volBtn))), r.volRow);
          talkRefs.set(c.id, r);
          return r.el;
        }),
      );
    }
    for (const [id, r] of talkRefs) {
      const speakers = peers.filter((p) => p.id !== me.id && p.talk.includes(`ch:${id}`));
      const listening = client.me.listen.includes(id);
      r.el.classList.toggle('incoming', speakers.length > 0 && listening);
      r.el.classList.toggle('muted-ch', !listening);
      r.who.textContent = speakers.length ? `${speakers.map((p) => p.name).join(', ')} ${speakers.length > 1 ? 'talk' : 'talks'}` : '';
      if (!client.talking.has(`ch:${id}`)) r.state.textContent = me.canTalk.includes(id) ? 'hold or tap' : 'listen only';
      if (r.listen) {
        r.listen.textContent = listening ? 'Listening' : 'Off';
        r.listen.classList.toggle('on', listening);
        r.listen.setAttribute('aria-pressed', String(listening));
      }
    }
    syncPtt();
  }

  function volumeRow(key, label) {
    const v = client.volume(key);
    const range = h('input', { type: 'range', min: 0, max: 1, step: 0.01, value: v.v, 'aria-label': `Volume ${label}`, oninput: (e) => client.setVolume(key, { v: Number(e.target.value) }) });
    const mute = h('button', { class: `small ${v.muted ? 'on-mute' : ''}`, onclick: () => (client.setVolume(key, { muted: !client.volume(key).muted }), mute.classList.toggle('on-mute', client.volume(key).muted), (mute.textContent = client.volume(key).muted ? 'Mute' : 'Audio')) }, v.muted ? 'Mute' : 'Audio');
    return h('div', { class: 'vol' }, mute, range);
  }

  // ---- incoming direct call ----
  function renderCall() {
    const me = client.me;
    if (!me) return;
    const callers = client.state.peers.filter((p) => p.talk.includes(`u:${me.id}`));
    callEl.hidden = !callers.length;
    if (!callers.length) return;
    const c = callers[0];
    fill(callEl, h('span', {}, h('b', {}, c.name), ' talks to you directly'), me.role.direct && ptt(h('button', { class: 'small' }, 'Reply (hold)'), `u:${c.id}`));
    syncPtt();
  }

  // ---- program ----
  let pgmKey = '';
  function renderProgram() {
    const prog = client.state.program || {};
    const allowed = client.me?.role.program;
    const key = JSON.stringify([allowed, prog, client.videoQuality || 'off']);
    if (key === pgmKey) return;
    pgmKey = key;
    if (!allowed || (!prog.audio && !prog.video)) return fill(pgmEl, h('h2', {}, 'Program'), h('p', { class: 'muted small' }, allowed ? 'No program feed.' : 'Your role does not receive the program.'));
    const q = client.videoQuality || 'off';
    fill(
      pgmEl,
      h('h2', {}, 'Program'),
      prog.audio && volumeRow('__program', 'Program'),
      prog.video &&
        h(
          'div',
          { class: 'seg', role: 'group', 'aria-label': 'Program video' },
          ...[
            ['off', 'Video off'],
            ['lo', 'Low'],
            ['hi', 'High'],
          ].map(([k, l]) => h('button', { class: `small ${q === k ? 'on' : ''}`, 'aria-pressed': String(q === k), onclick: () => client.setProgramVideo(k).then(() => ((pgmKey = ''), renderProgram()), (e) => toast(e.message)) }, l)),
        ),
      prog.video && q !== 'off' && video,
    );
  }

  // ---- team ----
  let teamKey = '';
  const teamRefs = new Map();
  const expanded = new Set();
  function renderTeam() {
    const me = client.me;
    if (!me) return;
    const { peers, roles } = client.state;
    const cams = client.tally.cams || [];
    const roleName = (id) => roles.find((r) => r.id === id)?.name || id;
    const camName = (id) => cams.find((c) => c.id === id)?.label;
    const admin = me.role.admin;
    const key = JSON.stringify([peers.map((p) => [p.id, p.name, p.role, p.cam]), me.role, me.id, admin && roles, admin && cams.map((c) => [c.id, c.label]), [...expanded]]);
    const editing = document.activeElement?.tagName === 'SELECT' && teamEl.contains(document.activeElement);
    if (key !== teamKey && !editing) {
      teamKey = key;
      teamRefs.clear();
      const act = (p) => p.catch((e) => toast(e.message));
      const others = peers.filter((p) => p.id !== me.id);
      fill(
        teamEl,
        h('div', { class: 'card-head' }, h('h2', {}, 'Participants'), h('span', { class: 'muted small' }, `${peers.length} online`)),
        !others.length && h('p', { class: 'muted small' }, 'Nobody else connected yet.'),
        h(
          'ul',
          { class: 'team' },
          ...[...others, peers.find((p) => p.id === me.id)].filter(Boolean).map((p) => {
            const r = { sub: h('span', { class: 'sub' }) };
            const isMe = p.id === me.id;
            const call = me.role.direct && !isMe && ptt(h('button', { class: 'small call', 'aria-label': `Talk directly to ${p.name} (hold)` }, 'Direct'), `u:${p.id}`);
            const sig = me.role.direct && !isMe && h('button', { class: 'sig-btn', 'aria-label': `Call ${p.name} (signal without audio)`, onclick: () => sendSignal(`u:${p.id}`, p.name) }, h('span', { 'aria-hidden': 'true' }, '◉'), 'Call');
            const gear = admin && h('button', { class: 'small ghost', 'aria-expanded': String(expanded.has(p.id)), 'aria-label': `Manage ${p.name}`, onclick: () => (expanded.has(p.id) ? expanded.delete(p.id) : expanded.add(p.id), renderTeam()) }, '···');
            const controls =
              admin &&
              expanded.has(p.id) &&
              h(
                'div',
                { class: 'mate-admin' },
                h('label', {}, 'Role', h('select', { onchange: (e) => (e.target.blur(), act(client.adminSetRole(p.id, e.target.value))) }, ...roles.map((x) => h('option', { value: x.id, selected: x.id === p.role }, x.name)))),
                h('label', {}, 'Tally', h('select', { onchange: (e) => (e.target.blur(), act(client.adminSetCam(p.id, e.target.value || null))) }, h('option', { value: '' }, 'no tally'), ...cams.map((c) => h('option', { value: c.id, selected: c.id === p.cam }, c.label)))),
                !isMe && h('button', { class: 'small danger', onclick: (e) => (e.currentTarget.dataset.armed ? act(client.adminKick(p.id)) : ((e.currentTarget.dataset.armed = '1'), (e.currentTarget.textContent = 'Really disconnect?'))) }, 'Disconnect'),
              );
            r.el = h('li', { class: `mate ${isMe ? 'me' : ''}` }, h('div', { class: 'mate-row' }, h('span', { class: 'led' }), h('div', { class: 'grow' }, h('div', { class: 'n' }, p.name, isMe && ' (you)'), r.sub), sig, call, gear), controls);
            teamRefs.set(p.id, r);
            return r.el;
          }),
        ),
      );
    }
    for (const p of peers) {
      const r = teamRefs.get(p.id);
      if (!r) continue;
      r.el.classList.toggle('talking', p.talk.length > 0);
      const toMe = p.talk.includes(`u:${me.id}`);
      r.sub.textContent = [roleName(p.role), camName(p.cam), p.remote ? (p.remote === 'online' ? 'via Cloud' : 'on site') : '', toMe ? 'talks to you' : p.talk.length ? 'talks' : '', !p.dc && p.id !== me.id ? 'slow path' : ''].filter(Boolean).join(' · ');
    }
    syncPtt();
  }

  // hybrid: if this side stays unreachable, offer to continue on the other side (Mac ↔ server)
  let lostSince = 0;
  let failoverEl = null;
  function checkFailover(st) {
    const down = !st.ws && st.media !== 'connected';
    lostSince = down ? lostSince || Date.now() : 0;
    const fb = client.session?.fallbackUrl;
    const show = down && fb && Date.now() - lostSince > 8000;
    if (show && !failoverEl) {
      const url = `${fb}?name=${encodeURIComponent(client.me?.name || '')}&auto=1`;
      failoverEl = h('div', { class: 'failover', role: 'alert' }, h('b', {}, 'Connection lost'), h('span', {}, 'This side of the hybrid session cannot be reached.'), h('a', { class: 'btn primary', href: url }, client.session.fallbackLabel || 'Continue on the other side'));
      document.body.append(failoverEl);
    } else if (!show && failoverEl) (failoverEl.remove(), (failoverEl = null));
  }
  const failoverTimer = setInterval(() => checkFailover(client.status), 2000);

  function renderStatus(st) {
    const ok = st.media === 'connected';
    const where = st.direct ? 'on site · direct' : client.canSwitch ? (st.path === 'lan' ? 'on site' : 'via Cloud') : '';
    const label = ok ? [st.ws ? 'connected' : 'connected · cloud offline', where].filter(Boolean).join(' · ') : st.media === 'connecting' ? 'connecting …' : st.media === 'reconnecting' ? 'interrupted …' : st.media === 'failed' ? 'error' : st.waiting || '';
    // hybrid: a visible button moves between the Mac on site and the cloud (the pill stays tappable too)
    const other = st.path === 'lan' ? 'cloud' : 'lan';
    const ask = other === 'lan' ? 'Continue on site, directly to the Mac? (only works in the venue Wi-Fi)' : 'Continue via the Cloud?';
    const go = () => confirm(ask) && client.switchPath(other);
    const pill = h('span', { class: `pill ${ok ? 'ok' : 'warn'} ${client.canSwitch ? 'switchable' : ''}`, title: client.canSwitch ? `Tap to continue ${other === 'lan' ? 'on site (directly to the Mac)' : 'via the Cloud'}` : st.dc ? 'Direct path to the SFU active' : '' }, label);
    if (client.canSwitch) pill.addEventListener('click', go);
    let sw = null;
    if (client.canSwitch && st.ws) {
      sw = h('button', { class: 'btn small path-switch', title: ask }, other === 'lan' ? '→ On site' : '→ Cloud');
      sw.addEventListener('click', go);
    } else if (client.session?.mode === 'hybrid' && client.session.fallbackUrl && st.path !== 'cloud') {
      // joined the Mac directly (local address): the cloud entry is a separate page
      const url = `${client.session.fallbackUrl}?name=${encodeURIComponent(client.me?.name || '')}&auto=1`;
      sw = h('a', { class: 'btn small path-switch', href: url, title: 'Continue via the Cloud' }, '→ Cloud');
      sw.addEventListener('click', (e) => confirm('Continue via the Cloud?') || e.preventDefault());
    }
    fill(connEl, pill, sw);
  }

  // ---- keyboard: 1–9 = channel N, Space = first talk channel ----
  const keyTargets = () => {
    const me = client.me;
    if (!me) return [];
    return client.state.channels.filter((c) => me.canListen.includes(c.id) || me.canTalk.includes(c.id)).map((c) => (me.canTalk.includes(c.id) ? `ch:${c.id}` : null));
  };
  const held = new Set();
  const onKey = (e) => {
    if (e.target.matches('input, select, textarea') || e.repeat) return;
    const idx = e.code === 'Space' ? keyTargets().findIndex(Boolean) : /^Digit[1-9]$/.test(e.code) ? Number(e.code.slice(5)) - 1 : -1;
    const target = keyTargets()[idx];
    if (!target) return;
    e.preventDefault();
    if (e.type === 'keydown' && !held.has(e.code)) {
      held.add(e.code);
      client.setTalk(target, true);
    } else if (e.type === 'keyup') {
      held.delete(e.code);
      client.setTalk(target, false);
    }
  };
  window.addEventListener('keydown', onKey);
  window.addEventListener('keyup', onKey);

  // ---- wiring ----
  let built = false;
  const renderAll = () => {
    if (!built) {
      built = true;
      startCard.replaceWith(body);
    }
    renderTally();
    renderCall();
    renderTalk();
    renderProgram();
    renderTeam();
  };
  client.on('joined', (s) => (nameEl.textContent = s.name));
  client.on('me', renderAll);
  client.on('state', () => client.me && (renderCall(), renderTalk(), renderProgram(), renderTeam()));
  client.on('talk', syncPtt);
  let nativeStatusTimer = null;
  if (APP && !client.nativeAudio) {
    let last = '';
    const pushStatus = (force = false) => {
      const names = [...client.talking].map((t) => (t.startsWith('ch:') ? client.state.channels.find((c) => `ch:${c.id}` === t)?.name : client.state.peers.find((p) => `u:${p.id}` === t)?.name)).filter(Boolean);
      const micPaused = !!client.rawMic?.muted;
      const text = micPaused ? 'Microphone paused · Open panel' : client.status.media !== 'connected' ? 'Connecting …' : names.length ? names.join(', ') : client.me?.role?.name || 'Connected';
      const st = { title: nameEl.textContent, text, talking: client.talking.size > 0 && !micPaused };
      const key = JSON.stringify(st);
      if (force === true || key !== last) ((last = key), native.update(st).catch(() => {}));
    };
    client.on('talk', pushStatus);
    client.on('me', pushStatus);
    client.on('joined', pushStatus);
    client.on('status', pushStatus);
    nativeStatusTimer = setInterval(() => pushStatus(true), 30000);
  }
  client.on('status', renderStatus);
  const switchText = { auto: 'Mac on site not reachable – continuing via the Cloud', moved: 'The session moved to the other Mac – reconnecting', 'lan-back': 'Back on site – talking directly to the Mac' };
  client.on('path-switch', (sw) => toast(switchText[sw.why] || (sw.to === 'cloud' ? 'Continuing via the Cloud' : 'Continuing on site'), 'ok'));
  client.on('tally', () => client.me && (renderTally(), renderTeam()));
  client.on('video', (stream) => {
    if (client.nativeAudio) { client.updateVideo?.(); return; }
    video.srcObject = stream;
    video.play().catch(() => {});
  });
  client.on('error', (m) => toast(m));

  // ---- call signal (flash/vibrate, no audio) ----
  async function sendSignal(target, label) {
    try {
      const r = await client.signal(target);
      toast(r.delivered ? `Call sent to ${label}` : `Nobody reached (${label})`, r.delivered ? 'ok' : 'err');
    } catch (e) {
      toast(e.message);
    }
  }
  let sigEl = null;
  client.on('signal', (m) => {
    sigEl?.remove();
    const text = m.target.startsWith('u:') ? `${m.from.name} is calling you` : `${m.from.name} is calling ${m.label}`;
    const ok = () => {
      sigEl?.remove();
      sigEl = null;
      navigator.vibrate?.(0);
    };
    sigEl = h('div', { class: 'signal', role: 'alertdialog', 'aria-live': 'assertive', onclick: ok }, h('div', { class: 'sig-text' }, text), h('button', { class: 'big' }, 'OK'));
    document.body.append(sigEl);
    navigator.vibrate?.([300, 150, 300, 150, 600]);
    setTimeout(() => sigEl && ok(), 20000);
  });

  // ---- audio settings (output device, ducking) ----
  async function audioSettings() {
    const devs = await navigator.mediaDevices.enumerateDevices().catch(() => []);
    const outs = devs.filter((d) => d.kind === 'audiooutput');
    const duck = h('input', { type: 'checkbox', id: 'duck', checked: client.ducking !== false, onchange: (e) => ((client.ducking = e.target.checked), (prefs.duck = e.target.checked), savePrefs(), client.setVolume('__program', {})) });
    const lvl = h('i');
    // squelch: threshold marker on the meter (meter shows peak × 1.4, linear) + live open/closed state
    const mark = h('b', { class: 'sq-mark' });
    const sqState = h('span', { class: 'sq-state' });
    const sqVal = h('span', { class: 'muted small' });
    const posOf = (db) => `${Math.min(100, 10 ** (db / 20) * 1.4 * 100)}%`;
    const sqRange = h('input', { type: 'range', min: -70, max: -20, step: 1, value: client.squelch || -50, 'aria-label': 'Squelch threshold' });
    const sqOn = h('input', { type: 'checkbox', checked: !!client.squelch });
    const applySq = () => {
      const db = sqOn.checked ? Number(sqRange.value) : 0;
      sqRange.disabled = !sqOn.checked;
      mark.hidden = !sqOn.checked;
      mark.style.left = posOf(Number(sqRange.value));
      sqVal.textContent = sqOn.checked ? `${sqRange.value} dB` : 'off';
      client.setSquelch(db).catch((e) => toast(`Squelch not available: ${e.message}`));
    };
    sqRange.addEventListener('input', applySq);
    sqOn.addEventListener('change', applySq);
    let raf = 0;
    const tick = () => {
      lvl.style.width = `${client.micLevel() * 100}%`;
      sqState.textContent = client.squelch ? (client.gateOpen ? 'open' : 'closed') : '';
      sqState.className = `sq-state ${client.squelch ? (client.gateOpen ? 'open' : 'shut') : ''}`;
      raf = requestAnimationFrame(tick);
    };
    const close = modal(
      h('h2', {}, 'Audio'),
      h('div', { class: 'field' }, h('span', { class: 'lbl' }, 'My microphone (level)'), h('div', { class: 'meter-big sq-meter' }, lvl, mark)),
      client.supportsSquelch !== false && h('div', { class: 'field' }, h('label', { class: 'check' }, sqOn, 'Squelch', h('span', { class: 'spacer' }), sqVal, sqState), sqRange, h('p', { class: 'muted small' }, 'The microphone only opens above the threshold (marker on the meter) – background noise stays out while a key is held or latched. While speaking it should show “open”, in pauses “closed”.')),
      SessionClient.canSelectOutput() && outs.length
        ? h('label', { class: 'field', for: 'out-sel' }, h('span', { class: 'lbl' }, 'Output'), h('select', { id: 'out-sel', onchange: (e) => client.setOutput(e.target.value).then(() => ((prefs.out = e.target.value), savePrefs()), (err) => toast(err.message)) }, ...outs.map((d) => h('option', { value: d.deviceId, selected: d.deviceId === (client.outputDeviceId || 'default') }, d.label || 'Output'))))
        : h('p', { class: 'muted small' }, 'Choose the output (headphones/speaker) in the system settings for this browser.'),
      h('label', { class: 'check' }, duck, 'Duck program while someone talks'),
      client.supportsProgramBuffer !== false && h('label', { class: 'field', for: 'pgm-buf' }, h('span', { class: 'lbl' }, 'Program buffer'), h('select', { id: 'pgm-buf', onchange: (e) => client.setProgramBuffer(e.target.value) }, ...[[0, 'Minimal – lowest delay'], [300, 'Normal (0.3 s)'], [800, 'Stable (0.8 s) – weak Wi-Fi'], [1500, 'Very stable (1.5 s) – mobile network']].map(([v, l]) => h('option', { value: v, selected: v === client.programBuffer }, l)))),
      client.supportsProgramBuffer !== false && h('p', { class: 'muted small' }, 'If the program picture stutters, raise the buffer. Talkback is not affected.'),
      APP && h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: appPrefs.handsFree, onchange: (e) => { appPrefs.handsFree = e.target.checked; client.configure?.({ handsFree: e.target.checked }); } }), 'Hands-free: keep talking when the app is in the background'),
      APP_PLATFORM === 'android' && h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: appPrefs.volumeKeys, onchange: (e) => ((appPrefs.volumeKeys = e.target.checked), native.update({ volumeKeys: e.target.checked })) }), 'Volume keys as talk key'),
      APP && h('p', { class: 'muted small' }, 'Headset and Bluetooth buttons: hold = talk, short press = latch, press again = release.'),
    );
    sqRange.disabled = !sqOn.checked;
    mark.hidden = !sqOn.checked;
    mark.style.left = posOf(Number(sqRange.value));
    sqVal.textContent = sqOn.checked ? `${sqRange.value} dB` : 'off';
    tick();
    const obs = new MutationObserver(() => !document.body.contains(lvl) && (cancelAnimationFrame(raf), obs.disconnect()));
    obs.observe(document.body, { childList: true });
    void close;
  }
  if (prefs.duck === false) client.ducking = false;
  const endScreen = (title, text) => {
    native.stop();
    clearInterval(nativeStatusTimer);
    fill(root, head, h('div', { class: 'center' }, h('div', { class: 'card narrow' }, h('h1', {}, title), text && h('p', {}, text), h('a', { class: 'btn primary', href: '#/' }, 'Back to sessions'))));
  };
  client.on('fatal', (m) => endScreen('Disconnected', m));
  client.on('ended', () => endScreen('Session ended'));
  client.on('kicked', () => endScreen('Disconnected', 'A session admin removed you from the session.'));
  // ---- hardware talk key (app): headset / Bluetooth button, volume keys; same rules as the screen keys ----
  // hold = talk, short press (<300 ms) = latch, press again = release; "toggle" (iOS headset click) = latch/release
  let hw = null; // { target, downAt, wasLatched }
  const offPtt = native.onPtt(({ action }) => {
    const target = latched.size ? [...latched][0] : keyTargets().find(Boolean);
    if (!target) return;
    if (action === 'toggle' || (action === 'down' && latched.has(target))) {
      if (action === 'down') hw = { target, wasLatched: true };
      if (latched.has(target)) {
        latched.delete(target);
        client.setTalk(target, false);
      } else if (action === 'toggle') {
        latched.add(target);
        client.setTalk(target, true);
      }
    } else if (action === 'down') {
      hw = { target, downAt: performance.now() };
      client.setTalk(target, true);
    } else if (action === 'up' && hw) {
      if (!hw.wasLatched) {
        if (performance.now() - hw.downAt < 300) latched.add(hw.target);
        else client.setTalk(hw.target, false);
      }
      hw = null;
    }
    navigator.vibrate?.(15);
    syncPtt();
  });

  // ---- background: iOS stops audio while the page is hidden/locked → warn and recover ----
  // in the app the native side keeps audio running: only the talk keys are released (unless hands-free is on)
  let hiddenAt = 0;
  const onVis = async () => {
    if (client.nativeAudio) {
      if (document.visibilityState === 'visible') client.recover().catch((e) => toast(e.message));
      return;
    }
    if (APP) {
      if (document.visibilityState === 'hidden') {
        if (!appPrefs.handsFree) {
          latched.clear();
          holds.clear();
          hw = null;
          if (client.talking.size) for (const t of [...client.talking]) client.setTalk(t, false);
          syncPtt();
        }
      } else client.recover(0);
      return;
    }
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      latched.clear();
      holds.clear();
      client.background();
      syncPtt();
      return;
    }
    client.recover(hiddenAt ? Date.now() - hiddenAt : 0);
    if (!wakeLock || wakeLock.released) wakeLock = await navigator.wakeLock?.request('screen').catch(() => null);
    if (hiddenAt && Date.now() - hiddenAt > 1500 && client.me) {
      const secs = Math.round((Date.now() - hiddenAt) / 1000);
      const banner = h('div', { class: 'bg-warn', role: 'alert' }, `The app was in the background for ${secs} s – audio and talk were interrupted. Checking the connection …`);
      document.body.append(banner);
      setTimeout(() => banner.remove(), 7000);
    }
    hiddenAt = 0;
  };
  document.addEventListener('visibilitychange', onVis);
  const onHide = () => client.leaveNow();
  window.addEventListener('pagehide', onHide);

  return () => {
    for (const [ev, fn] of [['keydown', onKey], ['keyup', onKey], ['pagehide', onHide], ['pointerup', releaseHold], ['pointercancel', releaseHold]]) window.removeEventListener(ev, fn);
    document.removeEventListener('visibilitychange', onVis);
    offPtt();
    client.unmountVideo?.();
    clearInterval(nativeStatusTimer);
    native.stop();
    wakeLock?.release?.();
    clearInterval(failoverTimer);
    failoverEl?.remove();
    if (!client.closed) client.leave();
  };
}
